import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getUtmParams } from '@/utils/utm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { reportErrorToBackend } from '@/utils/errors';
import { Mail, Lock, Eye, EyeOff, User, Gift, AlertCircle } from 'lucide-react';
import { trackSignupConversion, trackTrialStarted } from '@/utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.trustoffice.app';

// Use XMLHttpRequest for maximum mobile compatibility
const xhrPost = (url, data) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Accept', 'application/json');
    
    xhr.onreadystatechange = function() {
      if (xhr.readyState === 4) {
        try {
          const response = xhr.responseText ? JSON.parse(xhr.responseText) : {};
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(response);
          } else {
            reject(new Error(response.detail || `Request failed with status ${xhr.status}`));
          }
        } catch (e) {
          reject(new Error('Invalid server response'));
        }
      }
    };
    
    xhr.onerror = function() {
      reject(new Error('Network error - please check your connection'));
    };
    
    xhr.send(JSON.stringify(data));
  });
};

export default function SignUpPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [signupError, setSignupError] = useState('');
  
  // Referral code from URL params (e.g., /signup?ref=ABCD1234)
  const [referralCode, setReferralCode] = useState('');
  const [referralInfo, setReferralInfo] = useState(null);
  
  // Coupon code from URL params (e.g., /signup?coupon=WINGPOINT3M)
  const [couponCode, setCouponCode] = useState('');
  const [couponInfo, setCouponInfo] = useState(null);
  
  // WingPoint partnership params (e.g., /signup?ref=wp&wp_ref=WP-123&trust_name=Smith+Family+Trust&coupon=WINGPOINT3M)
  const [isWingPoint, setIsWingPoint] = useState(false);
  const [wingPointTrustName, setWingPointTrustName] = useState('');
  const [wingPointRef, setWingPointRef] = useState('');
  
  // Check for referral code, coupon, and WingPoint params on mount
  useEffect(() => {
    const refCode = searchParams.get('ref');
    
    // WingPoint partnership flow
    if (refCode && refCode.toLowerCase() === 'wp') {
      setIsWingPoint(true);
      const trustName = searchParams.get('trust_name') || '';
      const wpRef = searchParams.get('wp_ref') || '';
      const coupon = searchParams.get('coupon') || 'WINGPOINT3M';
      
      setWingPointTrustName(decodeURIComponent(trustName));
      setWingPointRef(wpRef);
      
      // Auto-apply WingPoint coupon
      const couponUpper = coupon.toUpperCase();
      setCouponCode(couponUpper);
      if (couponUpper === 'WINGPOINT3M') {
        setCouponInfo({
          code: 'WINGPOINT3M',
          description: '3 months free',
          regularPrice: '$79/month or $790/year',
          savings: 'Save $237 — 3 months free'
        });
      }
      
      // Store WingPoint ref for the backend
      if (wpRef) {
        sessionStorage.setItem('wp_ref', wpRef);
      }
      if (trustName) {
        sessionStorage.setItem('wp_trust_name', decodeURIComponent(trustName));
      }
    } else if (refCode) {
      setReferralCode(refCode.toUpperCase());
      validateReferralCode(refCode);
    }
    
    // Check for coupon parameter (non-WingPoint flow)
    const coupon = searchParams.get('coupon');
    if (coupon && !isWingPoint) {
      const couponUpper = coupon.toUpperCase();
      setCouponCode(couponUpper);
      
      // Set coupon info for known coupons
      if (couponUpper === 'TRUST49') {
        setCouponInfo({
          code: 'TRUST49',
          description: '$49/month for your first 3 months',
          regularPrice: '$79/month',
          savings: '$30 off per month'
        });
      } else if (couponUpper === 'WINGPOINT3M') {
        setCouponInfo({
          code: 'WINGPOINT3M',
          description: '3 months free',
          regularPrice: '$79/month or $790/year',
          savings: 'Save $237 — 3 months free'
        });
      }
    }
  }, [searchParams]);
  
  const validateReferralCode = async (code) => {
    try {
      const response = await fetch(`${API_URL}/api/referrals/validate/${code}`);
      if (response.ok) {
        const data = await response.json();
        if (data.valid) {
          setReferralInfo(data);
        }
      }
    } catch (error) {
      console.error('Failed to validate referral code:', error);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent double submission
    if (loading) return;
    
    setSignupError('');
    
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedName = name.trim();
    
    if (!trimmedEmail || !trimmedName || !password) {
      const msg = 'Please fill in all fields to create your account.';
      setSignupError(msg);
      toast.error(msg);
      return;
    }

    if (password !== confirmPassword) {
      const msg = 'Passwords do not match. Please re-enter your password in both fields.';
      setSignupError(msg);
      toast.error(msg);
      return;
    }

    if (password.length < 8) {
      const msg = 'Password must be at least 8 characters long for your account security.';
      setSignupError(msg);
      toast.error(msg);
      return;
    }
    if (!/[A-Za-z]/.test(password)) {
      const msg = 'Password must contain at least one letter.';
      setSignupError(msg);
      toast.error(msg);
      return;
    }
    if (!/\d/.test(password)) {
      const msg = 'Password must contain at least one number.';
      setSignupError(msg);
      toast.error(msg);
      return;
    }

    setLoading(true);
    
    try {
      // Step 1: Register using XMLHttpRequest (include referral code, WingPoint ref, and trust name if present)
      const wpRef = sessionStorage.getItem('wp_ref') || null;
      const wpTrustName = sessionStorage.getItem('wp_trust_name') || null;
      const utm = getUtmParams();
      await xhrPost(`${API_URL}/api/auth/register`, {
        email: trimmedEmail,
        password: password,
        name: trimmedName,
        referral_code: referralCode || null,
        wp_ref: wpRef,
        wp_trust_name: wpTrustName,
        utm_source: utm.utm_source || null,
        utm_campaign: utm.utm_campaign || null,
        utm_medium: utm.utm_medium || null,
        referrer: utm.referrer || null
      });
      
      // Step 2: Login using XMLHttpRequest
      const loginData = await xhrPost(`${API_URL}/api/auth/login`, {
        email: trimmedEmail,
        password: password
      });
      
      // Step 3: Store token and update user
      if (loginData.token) {
        localStorage.setItem('auth_token', loginData.token);
      }
      if (loginData.user) {
        setUser(loginData.user);
      }
      
      // Store coupon code for use after onboarding
      if (couponCode) {
        sessionStorage.setItem('pending_coupon', couponCode);
      }

      // If this came from the WingPoint connect flow, carry params to pricing
      // so we can redirect to the connect flow after checkout.
      const connectAfter = searchParams.get('connect_after');
      const connectWpRef = searchParams.get('wp_ref') || sessionStorage.getItem('wp_ref') || '';
      const connectTrustName = searchParams.get('trust_name') || sessionStorage.getItem('wp_trust_name') || '';
      const redirectUrl = searchParams.get('redirect_url') || '';

      if (connectAfter === '1' || isWingPoint) {
        // Build the connect flow URL to use after purchase
        const connectParams = new URLSearchParams();
        if (redirectUrl) connectParams.set('redirect_url', redirectUrl);
        if (connectWpRef) connectParams.set('wp_ref', connectWpRef);
        if (connectTrustName) connectParams.set('trust_name', connectTrustName);
        sessionStorage.setItem('wp_connect_after', '1');
        sessionStorage.setItem('wp_connect_params', connectParams.toString());
      }

      toast.success('Account created successfully');

      // Track signup conversion for Google Ads + GA4
      trackSignupConversion();

      // New users must subscribe — route to pricing page
      // If WingPoint flow, pass wp=1&plan=wingpoint to show exclusive plan
      if (isWingPoint || connectAfter === '1') {
        const pricingParams = new URLSearchParams({ wp: '1', plan: 'wingpoint' });
        if (couponCode) pricingParams.set('coupon', couponCode);
        navigate(`/pricing?${pricingParams.toString()}`);
      } else {
        navigate('/pricing');
      }
    } catch (error) {
      console.error('Signup error:', error);
      const msg = error.message || 'Could not create your account. Please try again or contact support@trustoffice.app for help.';
      setSignupError(msg);
      toast.error(msg);
      reportErrorToBackend(error, { operation: 'signup', page: 'SignUp' });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = () => {
    // Use TrustOffice's own Google OAuth (branded consent screen)
    const redirectAfter = '/onboarding';
    // Pass WingPoint attribution through the OAuth state so the backend
    // can store wp_ref on the user doc and fire the signup_completed webhook.
    const wpRef = sessionStorage.getItem('wp_ref') || '';
    const wpTrustName = sessionStorage.getItem('wp_trust_name') || '';
    let url = `${API_URL}/api/auth/google/login?redirect=${encodeURIComponent(redirectAfter)}`;
    if (wpRef) {
      url += `&wp_ref=${encodeURIComponent(wpRef)}`;
    }
    if (wpTrustName) {
      url += `&wp_trust_name=${encodeURIComponent(wpTrustName)}`;
    }
    window.location.href = url;
  };

  return (
    <div className="min-h-screen flex" data-testid="signup-page">
      {/* Left side - Texture with overlay */}
      <div className="hidden lg:flex lg:w-1/2 login-texture relative">
        <div className="absolute inset-0 login-overlay flex flex-col justify-center items-center p-12">
          <img 
            src="/assets/trustoffice-logo-vertical.svg?v=3"
            alt="TrustOffice"
            className="w-48 mb-8"
          />
          <p className="text-white/80 font-mono text-xs uppercase tracking-widest text-center max-w-md">
            Trust Governance Workspace
          </p>
          <div className="mt-16 text-white/60 font-mono text-[10px] uppercase tracking-widest">
            TrustOffice
          </div>
        </div>
      </div>

      {/* Right side - Sign up form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-subtle-bg">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden mb-12 text-center">
            <img 
              src="/assets/trustoffice-logo.svg?v=3"
              alt="TrustOffice"
              className="h-10 mx-auto"
            />
          </div>

          {/* Sign up card with corner marks */}
          <div className="card-trust corner-mark relative">
            {/* Coupon banner */}
            {couponInfo && !isWingPoint && !referralInfo && (
              <div className="bg-gradient-to-r from-gold/20 to-gold/10 -mx-6 -mt-6 px-6 py-4 mb-6 border-b border-gold/30">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xl">🎉</span>
                  <span className="font-medium text-navy">Special Offer Applied!</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  <span className="font-semibold text-gold">{couponInfo.description}</span>
                  {' '}(regularly {couponInfo.regularPrice}). 
                  <span className="text-navy"> Applied at checkout.</span>
                </p>
              </div>
            )}
            
            {/* WingPoint partnership banner */}
            {isWingPoint && (
              <div className="bg-gradient-to-r from-navy/10 to-blue-50 dark:from-navy/30 dark:to-blue-900/30 -mx-6 -mt-6 px-6 py-5 mb-6 border-b border-navy/20">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xl">🤝</span>
                  <span className="font-medium text-navy dark:text-white">Welcome from WingPoint</span>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  Your trust governance workspace is ready. Create your account to start managing your trust.
                </p>
                {wingPointTrustName && (
                  <p className="text-sm font-medium text-navy dark:text-white">
                    Trust: {wingPointTrustName}
                  </p>
                )}
                {couponInfo && (
                  <p className="text-sm text-gold mt-1">
                    ✦ {couponInfo.description} — applied at checkout
                  </p>
                )}
              </div>
            )}
            
            {/* Referral banner */}
            {referralInfo && (
              <div className="bg-gradient-to-r from-gold/20 to-warning/10 dark:from-gold/30 dark:to-warning/30 -mx-6 -mt-6 px-6 py-4 mb-6 border-b border-gold/30">
                <div className="flex items-center gap-2 mb-1">
                  <Gift className="w-5 h-5 text-gold" />
                  <span className="font-medium text-navy">You've been referred!</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {referralInfo.referrer_name} invited you to TrustOffice. 
                  Get <span className="font-semibold text-gold">{referralInfo.discount_percent}% off</span> your first payment!
                </p>
              </div>
            )}
            
            <h1 className="font-serif text-3xl text-navy mb-2">Create Account</h1>
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-8">
              Start managing your trusts
            </p>

            {/* Google sign up button */}
            <Button
              onClick={handleGoogleSignUp}
              className="w-full mb-6 bg-white border border-navy/20 text-navy hover:bg-navy hover:text-white font-sans uppercase tracking-wider text-xs h-12"
              data-testid="google-signup-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            {/* Divider */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 h-px bg-navy/10"></div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Or</span>
              <div className="flex-1 h-px bg-navy/10"></div>
            </div>

            {/* Email sign up form */}
            <form onSubmit={handleSignUp}>
              <div className="space-y-4">
                {/* Error state */}
                {signupError && (
                  <div className="p-3 bg-error/10 border border-error/20 flex items-start gap-2" data-testid="signup-error">
                    <AlertCircle className="w-4 h-4 text-error flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-error">Unable to create account</p>
                      <p className="text-xs text-error/80 mt-0.5">{signupError}</p>
                    </div>
                  </div>
                )}
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Full Name
                  </Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="pl-10 input-trust"
                      placeholder="John Smith"
                      required
                      data-testid="name-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Email Address
                  </Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 input-trust"
                      placeholder="your@email.com"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Password
                  </Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10 input-trust"
                      placeholder="Create password"
                      required
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-navy"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {/* Password requirements */}
                  <div className="mt-2 space-y-1">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Password must contain:</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      <span className={`text-xs flex items-center gap-1 ${password.length >= 8 ? 'text-success' : 'text-muted-foreground'}`}>
                        <span className={`w-3 h-3 flex items-center justify-center ${password.length >= 8 ? 'text-success' : 'text-muted-foreground/50'}`}>{password.length >= 8 ? '✓' : '○'}</span>
                        8+ characters
                      </span>
                      <span className={`text-xs flex items-center gap-1 ${/[A-Za-z]/.test(password) ? 'text-success' : 'text-muted-foreground'}`}>
                        <span className={`w-3 h-3 flex items-center justify-center ${/[A-Za-z]/.test(password) ? 'text-success' : 'text-muted-foreground/50'}`}>{/[A-Za-z]/.test(password) ? '✓' : '○'}</span>
                        1 letter
                      </span>
                      <span className={`text-xs flex items-center gap-1 ${/\d/.test(password) ? 'text-success' : 'text-muted-foreground'}`}>
                        <span className={`w-3 h-3 flex items-center justify-center ${/\d/.test(password) ? 'text-success' : 'text-muted-foreground/50'}`}>{/\d/.test(password) ? '✓' : '○'}</span>
                        1 number
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Confirm Password
                  </Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 input-trust"
                      placeholder="Confirm password"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full btn-primary h-12"
                  disabled={loading}
                  data-testid="signup-submit-btn"
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link to="/" className="text-navy font-medium hover:text-navy/70" data-testid="login-link">
                  Sign In
                </Link>
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Powered by TrustOffice
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
